import { HostMessageType, SubdomainType } from '@/constants'
import { onHostMessage, type HostInitPayload } from '@/utils/previewBridge'
import { getPreviewSubdomainType, isPreviewMode } from '@/utils/previewMode'
import router from './index'

export interface PreviewModule {
  install: () => void
  seed: (payload: HostInitPayload) => void
  whenConfigured: () => Promise<void>
  initialPath: () => string
}

export interface PreviewDescriptor {
  subdomainType: SubdomainType
  load: () => Promise<PreviewModule>
}

export const PREVIEW_PORTALS: PreviewDescriptor[] = [
  {
    subdomainType: SubdomainType.CustomerPortal,
    load: () => import('@/portals/order-management/preview'),
  },
]

export function resolvePreviewPortal(subdomainType: SubdomainType | null): PreviewDescriptor | undefined {
  if (subdomainType == null) {
    return undefined
  }
  return PREVIEW_PORTALS.find(portal => portal.subdomainType === subdomainType)
}

export function startPreviewSession(): () => void {
  if (!isPreviewMode()) {
    return () => {}
  }

  let started = false

  return onHostMessage(HostMessageType.Init, payload => {
    if (started) {
      return
    }
    const descriptor = resolvePreviewPortal(payload.subdomainType ?? getPreviewSubdomainType())
    if (!descriptor) {
      console.error(`No preview portal registered for subdomain type ${payload.subdomainType}`)
      return
    }
    started = true

    const boot = async () => {
      try {
        const module = await descriptor.load()
        module.install()
        module.seed(payload)
        await module.whenConfigured()
        await router.navigate(module.initialPath(), { replace: true })
      } catch (error) {
        started = false
        console.error('Failed to start preview session', error)
      }
    }
    boot()
  })
}
